import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store/appStore';
import {
  LayoutDashboard, Monitor, MapPin, Building2, Users, Bell, Zap, FileCode2,
  Shield, Package, MonitorSmartphone, Briefcase, FileText, ScrollText,
  UserCog, Settings, ChevronLeft,
} from 'lucide-react';

const navItems = [
  { section: 'Monitoring', items: [
    { to: '/', label: 'Overview', icon: LayoutDashboard },
    { to: '/devices', label: 'Devices', icon: Monitor },
    { to: '/sites', label: 'Sites', icon: MapPin },
    { to: '/clients', label: 'Clients', icon: Building2 },
    { to: '/map', label: 'Map', icon: MapPin },
    { to: '/alerts', label: 'Alerts', icon: Bell },
  ]},
  { section: 'Management', items: [
    { to: '/automation', label: 'Automation', icon: Zap },
    { to: '/scripts', label: 'Scripts', icon: FileCode2 },
    { to: '/patches', label: 'Patches', icon: Shield },
    { to: '/software', label: 'Software', icon: Package },
    { to: '/remote-sessions', label: 'Remote Sessions', icon: MonitorSmartphone },
    { to: '/jobs', label: 'Jobs', icon: Briefcase },
  ]},
  { section: 'Admin', items: [
    { to: '/reports', label: 'Reports', icon: FileText },
    { to: '/audit-log', label: 'Audit Log', icon: ScrollText },
    { to: '/users', label: 'Users & Roles', icon: UserCog },
    { to: '/settings', label: 'Settings', icon: Settings },
  ]},
];

export function Sidebar() {
  const collapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggle = useAppStore((s) => s.toggleSidebar);
  const location = useLocation();

  return (
    <aside
      className={cn(
        'flex flex-col border-r border-border bg-base-panel shrink-0 transition-all duration-200',
        collapsed ? 'w-16' : 'w-56',
      )}
    >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 h-14 border-b border-border shrink-0">
        <div className="h-7 w-7 rounded-md bg-accent flex items-center justify-center shrink-0">
          <span className="text-accent-fg font-bold text-sm">I</span>
        </div>
        {!collapsed && <span className="text-sm font-semibold">InsightNode</span>}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {navItems.map((group) => (
          <div key={group.section}>
            {!collapsed && (
              <h3 className="px-3 mb-1.5 text-2xs font-semibold text-text-disabled uppercase tracking-wider">
                {group.section}
              </h3>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={cn('nav-item', isActive && 'nav-item-active', collapsed && 'justify-center px-2')}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-border p-2 shrink-0">
        <button
          onClick={toggle}
          className={cn('nav-item w-full', collapsed && 'justify-center')}
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
